<template>
  <!--
    AB#21683 — the face of an agent, everywhere an agent speaks.

    One component, two skins: `agent="elsa"` wears the agent purple ring that
    ELSA has always had; `agent="atlas"` wears the Atlas blue from the
    artwork, kept one step lighter than the admin accent so "Atlas" and
    "selected" never read as the same colour. The portrait is a 256px WebP
    (~15 KB) shown at 2× so it stays crisp on retina, with a PNG fallback.

    States are the only animation, and they are deliberately small:
      idle      — the still portrait.
      thinking  — the ring turns and a soft halo breathes; used while a read
                  regenerates or a reply is being written.
      done      — a green dot: the read is current / the draft is ready.
    `pop` plays a short scale-in when the avatar first appears. Nothing moves
    while a person is reading, and everything is off under
    prefers-reduced-motion.

    The drawn ElsaAvatar this replaces argued for an illustration over a
    photo; the product owner chose portraits (AB#21683), and the rest of the
    design still says "generated, checkable, sometimes wrong" in words.
  -->
  <span
    class="agent-av"
    :class="[`agent-av--${agent}`, `agent-av--${state}`, { 'agent-av--pop': pop }]"
    :style="{ '--agent-av-size': `${size}px` }"
    :data-agent="agent"
    :data-state="state"
    role="img"
    :aria-label="label"
    data-test="agent-avatar"
  >
    <picture class="agent-av-pic">
      <source :srcset="webp" type="image/webp" />
      <img :src="png" :alt="''" :width="size" :height="size" draggable="false" />
    </picture>
    <span v-if="state === 'done'" class="agent-av-dot" aria-hidden="true"></span>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import atlasWebp from '@/assets/img/agents/atlas-256.webp';
import atlasPng from '@/assets/img/agents/atlas-256.png';
import elsaWebp from '@/assets/img/agents/elsa-256.webp';
import elsaPng from '@/assets/img/agents/elsa-256.png';

export type Agent = 'elsa' | 'atlas';
export type AgentState = 'idle' | 'thinking' | 'done';

const props = withDefaults(
  defineProps<{
    agent: Agent;
    /** Rendered size in px (the ring sits outside it). */
    size?: number;
    state?: AgentState;
    /** Play the short scale-in on mount. */
    pop?: boolean;
  }>(),
  { size: 28, state: 'idle', pop: false }
);

const webp = computed(() => (props.agent === 'atlas' ? atlasWebp : elsaWebp));
const png = computed(() => (props.agent === 'atlas' ? atlasPng : elsaPng));
const label = computed(() => {
  const name = props.agent === 'atlas' ? 'Atlas' : 'ELSA';
  if (props.state === 'thinking') return `${name} is thinking`;
  if (props.state === 'done') return `${name} — done`;
  return name;
});
</script>

<style lang="scss" scoped>
.agent-av {
  --agent-av-size: 28px;
  --ring: #7c5cd6;
  --ring-2: #b39cf5;
  position: relative;
  display: inline-grid;
  place-items: center;
  flex: none;
  width: var(--agent-av-size);
  height: var(--agent-av-size);
  border-radius: 50%;
  vertical-align: middle;
}
.agent-av--atlas {
  --ring: #3b8bff;
  --ring-2: #8fc0ff;
}
.agent-av-pic,
.agent-av img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: var(--surface-2);
}
/* The ring: a conic gradient in the agent's two tones, just outside the face. */
.agent-av::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, var(--ring), var(--ring-2), var(--ring));
  z-index: -1;
  opacity: 0.9;
}
/* A hairline of surface between ring and face so the ring reads as a ring. */
.agent-av::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1.5px var(--surface);
  pointer-events: none;
}
.agent-av-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 28%;
  height: 28%;
  min-width: 7px;
  min-height: 7px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 2px var(--surface);
}

/* appear */
.agent-av--pop {
  animation: agent-av-in 0.32s cubic-bezier(0.2, 0.9, 0.3, 1.3) both;
}
@keyframes agent-av-in {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
/* thinking: the ring turns, the halo breathes */
.agent-av--thinking::before {
  animation: agent-av-spin 1.6s linear infinite;
}
.agent-av--thinking {
  animation: agent-av-breathe 1.6s ease-in-out infinite;
}
@keyframes agent-av-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes agent-av-breathe {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--ring) 45%, transparent);
  }
  50% {
    box-shadow: 0 0 0 7px color-mix(in srgb, var(--ring) 0%, transparent);
  }
}
@media (prefers-reduced-motion: reduce) {
  .agent-av--pop,
  .agent-av--thinking,
  .agent-av--thinking::before {
    animation: none;
  }
}
</style>
